/**
 * geoPickHelper.ts
 * -------------------------------------------------------
 * 职责：echarts-gl Geo3D 坐标系的射线拾取。
 *       当点击未命中区块 mesh（如 scatter 层空白）时，
 *       用相机射线与地图顶面平面求交，再判断省份。
 * -------------------------------------------------------
 */

import type { ECharts } from 'echarts';
import glmatrix from 'claygl/src/dep/glmatrix';
import Plane from 'claygl/src/math/Plane';
import Ray from 'claygl/src/math/Ray';
import Vector3 from 'claygl/src/math/Vector3';
import { resolveProvinceAdcodeLoose } from '../provinceAdcode';
import type { Geo3DCoordSysLike } from '../types';

const vec3 = glmatrix.vec3;

/* -------- 类型 -------- */

/** Geo3D 坐标系上拾取所需的完整字段 */
type Geo3DPickingCoordSys = Geo3DCoordSysLike & {
    map: string;
    invTransform: Float64Array;
    getGeoBoundingRect: () => { x: number; y: number; width: number; height: number };
    getRegionByCoord: (coord: number[]) => { name: string } | undefined;
};

type ViewGLLike = {
    castRay: (x: number, y: number, out: typeof pickRay) => typeof pickRay;
};

/* -------- 预分配对象（避免每帧 GC） -------- */

const pickRay = new Ray();
const mapSurfacePlane = new Plane();
const pickHitWorld = new Vector3();
const pickDelta1 = vec3.create();
const pickDelta2 = vec3.create();

/* -------- 公共函数 -------- */

/** 获取 echarts-gl 的 Geo3D 坐标系实例 */
export function getGeo3DCoordinateSystem(
    chart: ECharts,
): Geo3DCoordSysLike | null {
    const internal = chart as unknown as {
        getModel: () => {
            getComponent: (
                mainType: string,
                idx?: number,
            ) => { coordinateSystem?: Geo3DCoordSysLike } | undefined;
        };
    };
    const comp = internal.getModel().getComponent('geo3D', 0);
    const cs = comp?.coordinateSystem;
    return cs && typeof cs.dataToPoint === 'function' ? cs : null;
}

/** 像素坐标 → geo3D 顶面经纬度（全国/省图均可） */
export function resolveLngLatFromGeo3DPixel(
    chart: ECharts,
    offsetX: number,
    offsetY: number,
): [number, number] | null {
    const geoRaw = getGeo3DCoordinateSystem(chart);
    if (!geoRaw) return null;

    const geo = geoRaw as Geo3DPickingCoordSys;
    if (!('viewGL' in geo)) return null;

    const viewGL = (geo as Geo3DPickingCoordSys & { viewGL?: ViewGLLike }).viewGL;
    if (!viewGL || !geo.invTransform) return null;

    const rect = geo.getGeoBoundingRect();
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const p0 = [0, 0, 0], p1 = [0, 0, 0], p2 = [0, 0, 0];
    geo.dataToPoint([cx, cy, 0], p0);
    geo.dataToPoint([cx + 0.5, cy, 0], p1);
    geo.dataToPoint([cx, cy + 0.5, 0], p2);

    vec3.sub(pickDelta1, p1, p0);
    vec3.sub(pickDelta2, p2, p0);
    vec3.cross(mapSurfacePlane.normal.array, pickDelta1, pickDelta2);
    vec3.normalize(mapSurfacePlane.normal.array, mapSurfacePlane.normal.array);
    mapSurfacePlane.normal._dirty = true;
    mapSurfacePlane.distance = vec3.dot(mapSurfacePlane.normal.array, p0);

    viewGL.castRay(offsetX, offsetY, pickRay);
    const hit = pickRay.intersectPlane(mapSurfacePlane, pickHitWorld);
    if (!hit) return null;

    const local = vec3.create();
    vec3.copy(local, hit.array);
    vec3.transformMat4(local, local, geo.invTransform);
    if (!Number.isFinite(local[0]) || !Number.isFinite(local[2])) return null;
    return [local[0], local[2]];
}

/**
 * 全国 geo3D 下，通过射线-平面求交 → 多边形内含判断，
 * 从像素坐标反推省份 adcode。
 */
export function resolveProvinceAdcodeFromChinaMapPixel(
    chart: ECharts,
    offsetX: number,
    offsetY: number,
): string | null {
    const geoRaw = getGeo3DCoordinateSystem(chart);
    if (!geoRaw) return null;
    const geo = geoRaw as Geo3DPickingCoordSys;
    if (geo.map !== 'china') return null;

    const lngLat = resolveLngLatFromGeo3DPixel(chart, offsetX, offsetY);
    if (!lngLat) return null;
    const region = geo.getRegionByCoord(lngLat);
    if (!region?.name) return null;
    return resolveProvinceAdcodeLoose(region.name);
}

/** 从 Geo3D 点击回调中提取像素坐标 */
export function pixelFromGeo3DClickParams(
    params: import('../types').Geo3DClickParams,
): { x: number; y: number } | null {
    const e = params.event as
        | { zrX?: number; zrY?: number; offsetX?: number; offsetY?: number }
        | undefined;
    if (!e) return null;
    if (typeof e.zrX === 'number' && typeof e.zrY === 'number') {
        return { x: e.zrX, y: e.zrY };
    }
    if (typeof e.offsetX === 'number' && typeof e.offsetY === 'number') {
        return { x: e.offsetX, y: e.offsetY };
    }
    return null;
}
